function _classPrivateMethodInitSpec(e, a) { _checkPrivateRedeclaration(e, a), a.add(e); }
function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
// ----------------------------------------------------------------------------
// Block: Radial Dials
// ----------------------------------------------------------------------------
// Nested theme blocks only render via `{% content_for 'blocks' %}`. Liquid
// cannot wrap later siblings, so this host wraps Dial rows into labelled
// lists client-side (storefront + theme editor).
// ----------------------------------------------------------------------------

import registerCustomElement from 'assets/scripts/helpers/register-custom-element';
var BODY_SELECTOR = '.radial-dials__body';
var DIAL_ROLE = 'dial';
var GROUP_LABEL_ROLE = 'group-label';
var WRAP_ATTR = 'data-radial-dials-wrap';
var LIST_SELECTOR = "ul[".concat(WRAP_ATTR, "]");
var ITEM_SELECTOR = "li[".concat(WRAP_ATTR, "]");
var LIST_CLASS = 'radial-dials__list';
var ITEM_CLASS = 'radial-dials__item';
function isDial(element) {
  return element.dataset.radialDialsRole === DIAL_ROLE;
}
function isGroupLabel(element) {
  return element.dataset.radialDialsRole === GROUP_LABEL_ROLE;
}
function groupLabelId(element) {
  if (element.classList.contains('radial-dials-group-label--empty')) {
    return '';
  }
  return element.id || '';
}
function createList(labelledById) {
  var list = document.createElement('ul');
  list.className = LIST_CLASS;
  list.setAttribute(WRAP_ATTR, '');
  if (labelledById) {
    list.setAttribute('aria-labelledby', labelledById);
  }
  return list;
}
function createItem() {
  var item = document.createElement('li');
  item.className = ITEM_CLASS;
  item.setAttribute(WRAP_ATTR, '');
  return item;
}

/**
 * Flatten the body back to the author order Liquid rendered, looking through
 * any list chrome a previous pass added.
 */
function readSequence(body) {
  var sequence = [];
  for (var child of Array.from(body.children)) {
    if (!child.matches(LIST_SELECTOR)) {
      sequence.push(child);
      continue;
    }
    for (var node of Array.from(child.children)) {
      if (node.matches(ITEM_SELECTOR)) {
        for (var dial of Array.from(node.children)) {
          sequence.push(dial);
        }
        continue;
      }

      // Theme editor may inject a group label inside the list before JS regroups.
      sequence.push(node);
    }
  }
  return sequence;
}

/**
 * Describe the target DOM: consecutive dials collapse into one list, labelled
 * by the most recent Group Label. Everything else stays a direct child.
 */
function planLayout(sequence) {
  var plan = [];
  var currentList = null;
  var currentLabelId = '';
  for (var node of sequence) {
    if (isDial(node)) {
      if (!currentList) {
        currentList = {
          type: 'list',
          labelId: currentLabelId,
          dials: []
        };
        plan.push(currentList);
      }
      currentList.dials.push(node);
      continue;
    }
    currentList = null;
    if (isGroupLabel(node)) {
      currentLabelId = groupLabelId(node);
    }
    plan.push({
      type: 'element',
      node
    });
  }
  return plan;
}
function listMatchesSlot(list, slot) {
  if (list.getAttribute('aria-labelledby') !== (slot.labelId || null)) {
    return false;
  }
  var items = Array.from(list.children);
  if (items.length !== slot.dials.length) {
    return false;
  }
  return items.every((item, index) => {
    return item.matches(ITEM_SELECTOR) && item.children.length === 1 && item.firstElementChild === slot.dials[index];
  });
}
function matchesPlan(body, plan) {
  var children = Array.from(body.children);
  if (children.length !== plan.length) {
    return false;
  }
  return plan.every((slot, index) => {
    var child = children[index];
    if (slot.type === 'element') {
      return child === slot.node;
    }
    return child.matches(LIST_SELECTOR) && listMatchesSlot(child, slot);
  });
}
function renderPlan(body, plan) {
  var fragment = document.createDocumentFragment();
  for (var slot of plan) {
    if (slot.type === 'element') {
      fragment.appendChild(slot.node);
      continue;
    }
    var list = createList(slot.labelId);
    for (var dial of slot.dials) {
      var item = createItem();
      item.appendChild(dial);
      list.appendChild(item);
    }
    fragment.appendChild(list);
  }
  body.replaceChildren(fragment);
}
function groupLists(host) {
  var body = host.querySelector(BODY_SELECTOR);
  if (!body) {
    return;
  }
  var plan = planLayout(readSequence(body));

  // Bail before touching the DOM so repeat passes never move a focused
  // tooltip trigger or rebuild chrome that is already correct.
  if (matchesPlan(body, plan)) {
    return;
  }
  renderPlan(body, plan);
}
var _observer = /*#__PURE__*/new WeakMap();
var _IamRadialDials_brand = /*#__PURE__*/new WeakSet();
class IamRadialDials extends HTMLElement {
  constructor() {
    super(...arguments);
    // Grouping mutates the very node the observer watches, so it is detached
    // first — otherwise the callback keeps re-entering itself and the queued
    // microtasks starve the event loop.
    _classPrivateMethodInitSpec(this, _IamRadialDials_brand);
    _classPrivateFieldInitSpec(this, _observer, null);
  }
  connectedCallback() {
    _assertClassBrand(_IamRadialDials_brand, this, _regroup).call(this);
  }
  disconnectedCallback() {
    var _classPrivateFieldGet2;
    (_classPrivateFieldGet2 = _classPrivateFieldGet(_observer, this)) === null || _classPrivateFieldGet2 === void 0 || _classPrivateFieldGet2.disconnect();
    _classPrivateFieldSet(_observer, this, null);
  }
}
function _regroup() {
  var _classPrivateFieldGet3;
  (_classPrivateFieldGet3 = _classPrivateFieldGet(_observer, this)) === null || _classPrivateFieldGet3 === void 0 || _classPrivateFieldGet3.disconnect();
  groupLists(this);
  _assertClassBrand(_IamRadialDials_brand, this, _observe).call(this);
}
function _observe() {
  var body = this.querySelector(BODY_SELECTOR);
  if (!body) {
    return;
  }
  if (!_classPrivateFieldGet(_observer, this)) {
    _classPrivateFieldSet(_observer, this, new MutationObserver(() => _assertClassBrand(_IamRadialDials_brand, this, _regroup).call(this)));
  }
  _classPrivateFieldGet(_observer, this).observe(body, {
    childList: true
  });
}
registerCustomElement('iam-radial-dials', IamRadialDials);